import { useEffect, useState } from 'react';
import { Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { ApiError, errorMessage } from '../../../../lib/api';
import {
  SignatureEditor,
  SignatureMailPreview,
  bodyOf,
  signatureLines,
} from '../../../settings/profile/SignatureEditor';
import { useSaveSignature, useSignature } from '../api';
import { ActionError, StepFrame, useWizard } from '../StepFrame';
import type { SignatureBody } from '../types';
import styles from './SignatureStep.module.css';

/** Nutzer 09 / M7: the company template filled by the server or free text; „Signatur übernehmen" stores and moves on. */
export function SignatureStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const signature = useSignature();
  const save = useSaveSignature();
  const [value, setValue] = useState<SignatureBody | null>(null);

  useEffect(() => {
    if (signature.data && value === null) setValue(bodyOf(signature.data));
  }, [signature.data, value]);

  async function take() {
    if (!value) return;
    try {
      await save.mutateAsync(value);
    } catch {
      return;
    }
    await wizard.act('continue');
  }

  const fieldError = save.error instanceof ApiError ? save.error.fields['text'] : undefined;
  const saveError = save.isError && !fieldError ? errorMessage(save.error) : null;
  const skip = {
    label: mobile ? 'Später' : 'Später einrichten',
    onClick: () => void wizard.act('skip'),
    loading: wizard.acting === 'skip',
  };
  const primary = {
    label: mobile ? 'Übernehmen' : 'Signatur übernehmen',
    onClick: () => void take(),
    loading: save.isPending || wizard.acting === 'continue',
    disabled: value === null,
  };
  const shown = signature.data && value ? signatureLines(signature.data, value) : null;

  return (
    <StepFrame
      title="Ihre E-Mail-Signatur"
      text="Die Vorlage der Firma ist mit Ihren Angaben gefüllt. Sie können sie übernehmen oder frei schreiben."
      mobileTitle="E-Mail-Signatur"
      mobileText="Firmenvorlage mit Ihren Angaben gefüllt."
      width={900}
      gap={22}
      footer={{
        back: wizard.goBack ? { label: 'Zurück', onClick: wizard.goBack } : undefined,
        secondary: skip,
        primary,
      }}
    >
      {signature.data && value ? (
        mobile ? (
          <SignatureEditor
            signature={signature.data}
            value={value}
            onChange={setValue}
            variant="mobile"
            error={fieldError}
          />
        ) : (
          <div className={styles.grid}>
            <div className={styles.editorCard}>
              <SignatureEditor
                signature={signature.data}
                value={value}
                onChange={setValue}
                variant="wizard"
                error={fieldError}
              />
            </div>
            <div className={styles.side}>
              <SignatureMailPreview
                lines={shown?.lines ?? []}
                imageUrl={value.embedImage ? signature.data.preview.imageUrl : null}
              />
              <div className={styles.note}>
                Die Signatur gilt für Mails, die Sie aus DAMOKLES senden. Ihr eigenes Mailprogramm bleibt
                unberührt.
              </div>
            </div>
          </div>
        )
      ) : signature.isError ? (
        <p className={styles.error}>{errorMessage(signature.error)}</p>
      ) : (
        <Skeleton variant="block" height={mobile ? 300 : 314} />
      )}
      {saveError ? (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      ) : null}
      <ActionError />
    </StepFrame>
  );
}
